import type {
    RatioDailyAggregatedItem,
    RatioEvents,
    RatioEventItems,
} from '@/types/AnalysisResultType';
import { calculatePeriodComparison } from '@/utils/periodComparison';

export interface ClientFilterParams {
    keyword?: string;
}

// ---------------------------------------------------------------------------
// バックエンド CalculationService と同じ丸め・割合計算
// ---------------------------------------------------------------------------

/** RatioService / CalculationService.roundToFirstDecimal と同等（実装は Math.round） */
function roundToFirstDecimal(value: number): number {
    return Math.round(value);
}

/** CalculationService.getPercentage と同等 */
function getPercentage(part: number, total: number): number {
    return total > 0 ? Math.round((part / total) * 100) : 0;
}

/** CalculationService.getAverage と同等（平均を小数第1位で四捨五入→整数） */
function getAverage(total: number, count: number): number {
    return count > 0 ? roundToFirstDecimal(total / count) : 0;
}

/**
 * AnalysisController と同様: start/end は日付文字列（YYYY-MM-DD）で両端含む。
 * ChronoUnit.DAYS.between(start.atStartOfDay, end.plusDays(1).atStartOfDay) に相当。
 */
function periodDaysInclusive(start: string, end: string): number {
    const [ys, ms, ds] = start.split('-').map(Number);
    const [ye, me, de] = end.split('-').map(Number);
    const s = Date.UTC(ys, ms - 1, ds);
    const e = Date.UTC(ye, me - 1, de);
    return Math.max(1, Math.floor((e - s) / 86400000) + 1);
}

/**
 * 日別集計データから指定期間を切り出し、RatioEvents を構築する。
 * キーワード・色フィルタが指定されている場合はイベントタイトルで部分一致フィルタする。
 * 数値の丸め・割合はバックエンド RatioService / CalculationService に揃える。
 */
export function aggregateDailySlice(
    allDaily: RatioDailyAggregatedItem[],
    start: string,
    end: string,
    filters: ClientFilterParams = {},
): RatioEvents {
    const filtered = allDaily.filter((d) => d.date >= start && d.date <= end);

    const shouldFilterByKeyword = !!filters.keyword && filters.keyword.trim() !== '';

    const applyEventFilter = (items: { title: string; totalTime: number; percentage: number }[]) => {
        if (!shouldFilterByKeyword) return items;
        const kw = filters.keyword!.toLowerCase();
        return items.filter((ev) => ev.title.toLowerCase().includes(kw));
    };

    // RatioService.buildDailyBreakdown と同様: 各イベント時間を丸め → 日合計 → 日別割合 → 日合計を再丸め
    const dailyAggregatedData: RatioDailyAggregatedItem[] = filtered.map((day) => {
        const events = applyEventFilter(day.eventItems);
        const roundedEvents = events.map((ev) => ({
            title: ev.title,
            totalTime: roundToFirstDecimal(ev.totalTime),
            percentage: 0,
        }));
        const dayTotal = roundedEvents.reduce((sum, ev) => sum + ev.totalTime, 0);
        const withPercentage = roundedEvents.map((ev) => ({
            ...ev,
            percentage: getPercentage(ev.totalTime, dayTotal),
        }));
        return {
            date: day.date,
            totalTime: roundToFirstDecimal(dayTotal),
            eventItems: withPercentage,
        };
    });

    // タイトルごとに期間合計・出現日数（count は日別ブロック数。元データが日次のみのためイベント件数とは一致しない場合あり）
    const eventMap = new Map<string, { totalTime: number; count: number }>();
    for (const day of dailyAggregatedData) {
        for (const ev of day.eventItems) {
            const existing = eventMap.get(ev.title);
            if (existing) {
                existing.totalTime += ev.totalTime;
                existing.count += 1;
            } else {
                eventMap.set(ev.title, { totalTime: ev.totalTime, count: 1 });
            }
        }
    }

    // RatioService: 各イベント名の合計時間を四捨五入してから全体合計
    for (const agg of eventMap.values()) {
        agg.totalTime = roundToFirstDecimal(agg.totalTime);
    }

    const grandTotal = Array.from(eventMap.values()).reduce((s, v) => s + v.totalTime, 0);
    const periodDays = periodDaysInclusive(start, end);

    const ratioEventItems: RatioEventItems[] = Array.from(eventMap.entries())
        .filter(([, agg]) => agg.totalTime > 0)
        .map(([title, agg]) => {
            const evTotal = agg.totalTime;
            const count = agg.count;
            return {
                title,
                totalTime: evTotal,
                percentage: getPercentage(evTotal, grandTotal),
                count,
                averageTimePerCount: getAverage(evTotal, count),
                averageTimePerDay: getAverage(evTotal, periodDays),
            };
        });

    ratioEventItems.sort((a, b) => b.percentage - a.percentage);

    const totalCount = ratioEventItems.reduce((s, r) => s + r.count, 0);

    return {
        ratioEventItems,
        totalTime: grandTotal,
        averageTime: roundToFirstDecimal(getAverage(grandTotal, totalCount)),
        totalCount,
        dailyAggregatedData,
    };
}

/**
 * 同じフィルタで集計した前期の RatioEvents と突き合わせ、スコアカード・ランキング用の前期比を付与する。
 */
export function applyPreviousPeriodRatioComparisons(current: RatioEvents, previous: RatioEvents): RatioEvents {
    const prevByTitle = new Map(previous.ratioEventItems.map((r) => [r.title, r]));

    const ratioEventItems: RatioEventItems[] = current.ratioEventItems.map((item) => {
        const p = prevByTitle.get(item.title);
        return {
            ...item,
            totalTimeComparison: calculatePeriodComparison(item.totalTime, p?.totalTime ?? 0),
            countComparison: calculatePeriodComparison(item.count, p?.count ?? 0),
        };
    });

    return {
        ...current,
        ratioEventItems,
        totalTimeComparison: calculatePeriodComparison(current.totalTime, previous.totalTime),
        averageTimeComparison: calculatePeriodComparison(current.averageTime, previous.averageTime),
        totalCountComparison: calculatePeriodComparison(current.totalCount, previous.totalCount),
    };
}
